'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { reportClientError } from '@/lib/reportClientError';
import { recoverFromChunkError } from '@/lib/chunkReload';

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
    reportClientError(error, 'route-error-boundary');
    // A stale-deploy chunk error is not a broken page, it is an old tab. Report
    // it first — the reload discards this document, and `keepalive` on the
    // report is what lets it survive that — then reload into the current build.
    void recoverFromChunkError(error);
  }, [error]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="glass w-full max-w-md rounded-card p-8 text-center sm:p-10">
        <div
          aria-hidden
          className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-coral-500/10"
        >
          <AlertTriangle className="h-6 w-6 text-coral-300" />
        </div>
        <h1 className="font-display text-3xl tracking-tight text-ink">Something went wrong</h1>
        <p className="mt-3 text-ink-muted">
          An unexpected error interrupted this page. It&apos;s usually temporary — trying again
          often clears it up.
        </p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Button size="lg" onClick={reset}>
            Try again
          </Button>
          <Button variant="secondary" size="lg" asChild>
            <Link href="/">Go home</Link>
          </Button>
        </div>
        {error.digest ? (
          <div className="mt-6 rounded-xl border border-coral-500/30 bg-coral-500/10 px-4 py-3 text-xs text-coral-300">
            <span className="font-semibold">Reference:</span>{' '}
            <code className="font-mono">{error.digest}</code>
          </div>
        ) : null}
      </div>
    </div>
  );
}
